import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Programatically uploading to blobstore in python",
  "description": "",
  "published": "2010-08-10T00:00:00.000Z",
  "redirect_from": ["/blog/programatically-uploading-to-blobstore-in-python/swizec/1423"],
  "categories": "Application programming interface, django, Form (web), Google AppEngine, python, Uncategorized, Uploading and downloading",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Officially this is something that cannot be done. Or rather that shouldn't be done. When you look at the `}<a parentName="p" {...{
        "href": "http://code.google.com/appengine/",
        "title": "Google App Engine"
      }}>{`google appengine`}</a>{` docs on "`}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Uploading_and_downloading",
        "title": "Uploading and downloading"
      }}>{`uploading`}</a>{` to blobstore" this is what they have to say:`}</p>
    <blockquote>
      <p parentName="blockquote">{`Blobs are useful for serving large files, such as video or image files, and for allowing users to upload large data files.`}</p>
      <p parentName="blockquote">{`To prompt a user to upload a Blobstore value, your app presents a `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Form_%28web%29",
          "title": "Form (web)"
        }}>{`web form`}</a>{` with a file upload field.`}</p>
    </blockquote>
    <p>{`So ok, obviously the official documentation isn't of much use here since it only talks about letting users upload files. But I needed something different. I needed to fetch an image from an url (gotten by intricate means, different story) and store it in the blobstore so it could later be served to many users. Obviously since `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/File_system",
        "title": "File system"
      }}>{`file access`}</a>{` isn't permitted on appengine the only choice left was storing the file in the blobstore.`}</p>
    <p>{`Naturally someone else has had this problem before right?`}</p>
    <p>{`No. There are no solutions I could find online. None. Nada. Zilch. Niente.`}</p>
    <p>{`After a few hours of hacking a week or so ago, however, I got it working.`}</p>
    <p>{`Essentially the solution is to fake a form post to the blobstore url the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Application_programming_interface",
        "title": "Application programming interface"
      }}>{`API`}</a>{` creates. An interesting gotcha is that a redirect happens. Initially I thought I was making the form post right back to my application, but apparently you're first posting to the blobstore, then the blobstore posts back to you. For some reason I couldn't keep the associated meta-data to go through with the request so there's an ugly-ish workaround that happens.`}</p>
    <p>{`Another thing that's important to note for this tutorial/howto is that I am using django-nonrel and that the initial event that starts the process is triggered by appengine's task queue.`}</p>
    <h4 {...{
      "id": "the-howto"
    }}>{`The howto`}</h4>
    <p>{`First, these are all the imports I'm using, there's quite a few, so heh :)`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from django.http import HttpResponse, HttpResponseBadRequest, HttpRequest
from django.views.decorators.csrf import csrf_exempt
from django.conf import settings
from django.core.urlresolvers import reverse
import simplejson as json
import urllib2, urllib
from cStringIO import StringIO

from google.appengine.api.urlfetch import ResponseTooLargeError, DownloadError
from google.appengine.ext import blobstore
from google.appengine.api import urlfetch

from forms import ArticleProcForm
from models import Article
from lib import ImageExtractor
from lib import urllib2_file
from lib.urllib2_file import UploadFile
from lib.decorators import form_valid
`}</code></pre>
    <p>{`First thing that you're going to need is the function that starts the whole process (in my case this is a django view)`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`class ArticleProcForm(forms.Form):
    article = forms.IntegerField(required=True)

@csrf_exempt
@form_valid(ArticleProcForm, 'POST')
def article(request):
    try:
        article = Article.objects.get(id=request.form.cleaned_data['article'])
    except Article.DoesNotExist:
        return HttpResponse(json.dumps({'status': 'Bad Article'}))

    try:
        image_url = ImageExtractor.getImages(article.url)[0]['url']
    except IndexError:
        pass
    else:
        # important bit
        try:
            image = StringIO(urllib2.urlopen(image_url).read())
        except (urllib2.HTTPError, DownloadError):
            pass
        else:
            image = UploadFile(image, '.'.join([str(article.id), image_url.rsplit('.', 1)[1][:4]]))
            upload_url = blobstore.create_upload_url(reverse('Articles.views.upload'))

            try:
                urllib2.urlopen(upload_url, {'file': image})
            except (DownloadError, RequestTooLargeError):
                pass
        # end of important bit

    return HttpResponse(json.dumps({'status': 'OK'}))
`}</code></pre>
    <p>{`Here is basically what happens in the important bit:`}</p>
    <ol>
      <li parentName="ol">{`Download image from url and change it to a StringIO`}</li>
      <li parentName="ol">{`Make an UploadFile (basically a bundle of `}<a parentName="li" {...{
          "href": "http://en.wikipedia.org/wiki/String_%28computer_science%29",
          "title": "String (computer science)"
        }}>{`byte-string`}</a>{`-data and desired filename)`}</li>
      <li parentName="ol">{`Create an upload_url with the blobstore API`}</li>
      <li parentName="ol">{`Fake a file-upload form post`}</li>
    </ol>
    <p>{`The next thing we need is a view that will handle the request the blobstore will send back to our app.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`@csrf_exempt
def upload(request):
    if request.method == 'POST':
        blobs = get_uploads(request, field_name='file', populate_post=True)

        article = Article.objects.get(id=int(blobs[0].filename.split('.')[0]))
        article.media = blobs[0].filename
        article.parsed = True
        article.save()

        return HttpResponseRedirect(reverse('Articles.views.upload'))
    else:
        return HttpResponse('meow')
`}</code></pre>
    <p>{`Basically it extracts the article's id from the filename (the only way I could make work to pass that information) and stores some changes into the datastore. You'll notice that I'm basically just storing the article's id again in another field, this is to preserve knowledge of the file extension. It's also important to note that the blobstore requires a redirect response upon success, otherwise it will throw an error.`}</p>
    <p>{`Here is the get_uploads function I found online somewhere.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def get_uploads(request, field_name=None, populate_post=False):
    """Get uploads sent to this handler.
    Args:
      field_name: Only select uploads that were sent as a specific field.
      populate_post: Add the non blob fields to request.POST
    Returns:
      A list of BlobInfo records corresponding to each upload.
      Empty list if there are no blob-info records for field_name.
    """

    if hasattr(request,'__uploads') == False:
        request.META['wsgi.input'].seek(0)
        fields = cgi.FieldStorage(request.META['wsgi.input'], environ=request.META)

        request.__uploads = {}
        if populate_post:
            request.POST = {}

        for key in fields.keys():
            field = fields[key]
            if isinstance(field, cgi.FieldStorage) and 'blob-key' in field.type_options:
                request.__uploads.setdefault(key, []).append(blobstore.parse_blob_info(field))
            elif populate_post:
                request.POST[key] = field.value
    if field_name:
        try:
            return list(request.__uploads[field_name])
        except KeyError:
            return []
    else:
        results = []
        for uploads in request.__uploads.itervalues():
            results += uploads
        return results
`}</code></pre>
    <p>{`Now the process of serving this blob to the browser is very simple and goes something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`class ImageForm(forms.Form):
    id = forms.CharField(required=True)

@form_valid(ImageForm, 'GET')
@cache_response
def image(request):
    blob = BlobInfo.gql("WHERE filename='%s' LIMIT 1" % request.form.cleaned_data['id'])[0]

    return HttpResponse(BlobReader(blob.key()).read(),
                        content_type=blob.content_type)
`}</code></pre>
    <h4 {...{
      "id": "one-final-note"
    }}>{`One final note`}</h4>
    <p>{`And one VERY important final note. The vanilla urllib2 library can't handle file uploads, so I found one online that can. It's called `}<a parentName="p" {...{
        "href": "http://fabien.seisen.org/python/urllib2_file/"
      }}>{`urllib2_file`}</a>{`.`}</p>
    <p>{`However it doesn't quite work on google appengine. For example it can't handle being told what you want the filename to be and some other details because it relies on raw file access. So I changed it a little bit, unfortunately I don't quite know how to upstream my changes so I'm hosting it on github.`}</p>
    <p>{`You can get it `}<a parentName="p" {...{
        "href": "http://github.com/Swizec/urllib2_file"
      }}>{`at github`}</a>{`, feel free to contribute.`}</p>
    <h6 {...{
      "id": "related-articles-by-zemanta"
    }}>{`Related articles by Zemanta`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.notdot.net/2010/08/Using-BlobReader-wildcard-subdomains-and-webapp2"
        }}>{`Using BlobReader, wildcard subdomains and webapp2 on Google AppEngine`}</a>{` (notdot.net)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/django-protip-2-forms-are-awesome/swizec/1410"
        }}>{`Django protip #2: Forms are awesome`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://googleappengine.blogspot.com/2010/06/app-engine-sdk-135-released-with-new.html"
        }}>{`App Engine SDK 1.3.5 Released With New Task Queue, Python Precompilation, and Blob Features`}</a>{` (googleappengine.blogspot.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://debuggable.com/posts/parsing-file-uploads-at-500-mb-s-with-node-js:4c03862e-351c-4faa-bb67-4365cbdd56cb"
        }}>{`Parsing file uploads at 500 mb/s with node.js`}</a>{` (debuggable.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=82ae7610-8dea-47af-9e81-485f91ffacd6",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      